/**
 * ScheduleSpreadHeatmap (Spec 201) — a 7×24 weekly heatmap of when backups are scheduled to
 * START, one count per per-entity schedule the executor runs, so an operator can SEE clustering
 * and shift a backup's start time to avoid flooding Fabric. Cells over the concurrency cap are red.
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { LoadingState, ErrorState, EmptyState } from '@/components/ui';
import { schedulesApi } from '../../api/schedules';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const ScheduleSpreadHeatmap: React.FC<{
  /** Click a cell to drill into the backups that run in that hour. */
  onCellClick?: (dow: number, hour: number) => void;
  /** Highlight the currently-filtered hour column. */
  selectedHour?: number | null;
}> = ({ onCellClick, selectedHour = null }) => {
  // Always reflect the latest schedule: refetch on mount + window focus, never serve stale, so any
  // change (start-time edit here, or a plan/schedule edit on another tab) updates the heatmap.
  const q = useQuery({
    queryKey: ['backup', 'schedule-spread'],
    queryFn: schedulesApi.spread,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  if (q.isLoading) return <LoadingState rows={4} />;
  if (q.isError) return <ErrorState onRetry={() => q.refetch()} />;
  const data = q.data;
  if (!data || data.total === 0) {
    return (
      <EmptyState
        title="No scheduled backups yet"
        message="Give a plan a schedule (Plans tab) to see how its backups spread across the week — and whether any hour exceeds the concurrency cap."
      />
    );
  }
  const { cells, cap, max } = data;
  // Spec 214 FR-040: monthly crons live in their OWN row (day-of-month honored server-side —
  // they no longer smear across every weekday) and all cells share one labeled timezone.
  const monthly: number[] = data.monthly ?? new Array(24).fill(0);
  const hasMonthly = monthly.some((n) => n > 0);
  const tz = data.timezone ?? 'America/New_York';
  // The concurrency cap governs backups starting TOGETHER, so over/under-cap status is driven by the
  // per-minute peak (5-minute staggering keeps it low), not the hourly volume. Fall back to the
  // hourly count if an older backend doesn't send peaks.
  const peaks = data.peaks ?? cells;

  // 0 = empty; peak under cap = green ramp; peak over cap = red ramp (flooding risk).
  const cellColor = (peak: number): string => {
    if (peak <= 0) return 'var(--color-bg-subtle)';
    if (peak > cap) return `rgba(239,68,68,${Math.min(1, 0.55 + (0.45 * (peak - cap)) / Math.max(1, max - cap))})`;
    return `rgba(16,185,129,${0.25 + (0.6 * peak) / Math.max(1, cap)})`;
  };

  const overCapHours = peaks.reduce((n, row) => n + row.filter((p) => p > cap).length, 0);

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="border-separate" style={{ borderSpacing: '3px' }}>
          <thead>
            <tr>
              <th className="w-10" />
              {Array.from({ length: 24 }, (_, h) => (
                <th key={h} className="text-[10px] font-normal text-[var(--color-text-secondary)]">
                  {h % 3 === 0 ? String(h).padStart(2, '0') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cells.map((row, d) => (
              <tr key={d}>
                <td className="pr-2 text-right text-xs text-[var(--color-text-secondary)]">{DOW[d]}</td>
                {row.map((c, h) => {
                  const isSel = selectedHour === h;
                  const peak = peaks[d]?.[h] ?? c;
                  return (
                    <td key={h}>
                      <div
                        role={onCellClick ? 'button' : undefined}
                        onClick={onCellClick ? () => onCellClick(d, h) : undefined}
                        className={`h-5 w-5 rounded-sm border ${
                          isSel ? 'border-[var(--color-accent)] ring-1 ring-[var(--color-accent)]' : 'border-[var(--color-border)]'
                        } ${onCellClick ? 'cursor-pointer' : ''}`}
                        style={{ backgroundColor: cellColor(peak) }}
                        title={`${DOW[d]} ${String(h).padStart(2, '0')}:00 · ${c} backup${c === 1 ? '' : 's'} this hour · peak ${peak} starting together${peak > cap ? ` — over cap (${cap})` : ''}${onCellClick ? ' · click to filter' : ''}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
            {hasMonthly && (
              <tr>
                <td className="pr-2 text-right text-xs text-[var(--color-text-secondary)]" title="Monthly cadences fire on specific days of the month — not every week">
                  Monthly
                </td>
                {monthly.map((c, h) => (
                  <td key={h}>
                    <div
                      className="h-5 w-5 rounded-sm border border-[var(--color-border)]"
                      style={{ backgroundColor: c > 0 ? 'rgba(99,102,241,0.55)' : 'var(--color-bg-subtle)' }}
                      title={`Monthly · ${String(h).padStart(2, '0')}:00 · ${c} backup${c === 1 ? '' : 's'} (fires on its day of the month)`}
                    />
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--color-text-secondary)]">
        <span>All times {tz}. Color = most backups starting together (per minute) vs the concurrency cap {cap}; backups are spread within the hour</span>
        <span className="flex items-center gap-1">
          <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: 'rgba(16,185,129,0.65)' }} /> under cap
        </span>
        <span className="flex items-center gap-1">
          <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: 'rgba(239,68,68,0.85)' }} /> over cap — rebalance
        </span>
        {overCapHours > 0 && (
          <span className="text-[var(--color-status-warning)]">⚠ {overCapHours} hour{overCapHours === 1 ? '' : 's'} have a start-burst over the cap</span>
        )}
      </div>
    </div>
  );
};

export default ScheduleSpreadHeatmap;
